import { useApiUrl } from "@pankod/refine-core";
import { useEffect, useState } from "react";
import { IBranchOffices } from "interfaces";
import { axiosInstance } from "services/axios";

/**
 * Identificação da filial na Recarga de Planos (spec 036, RN11).
 *
 * O `consumer_log` grava só o `bo_id` (`"010"`, zero-padded a partir de `BranchOfficeId`), e
 * decorar 30+ códigos é trabalho do operador que a tela pode poupar.
 *
 * `BranchOfficeId` **não é chave única** — a PK de `BranchOffice` é `AreaId`, e uma filial agrupa
 * N áreas. Logo o rótulo é `UF · N áreas`, com as áreas no tooltip: escolher uma
 * `AreaDescription` isolada seria informação falsa.
 */

export interface BranchOfficeLabel {
  /** UFs distintas da filial, ordenadas. Normalmente uma. */
  ufs: string[];
  /** Descrições das áreas, ordenadas. */
  areas: string[];
}

export type BranchOfficeLabels = Record<string, BranchOfficeLabel>;

/** `bo_id` chega zero-padded em 3 dígitos; a origem é numérica. */
const normalizeId = (branchOfficeId: number | string): string =>
  String(Number(branchOfficeId)).padStart(3, "0");

export const aggregateBranchOfficeLabels = (
  branchOffices: IBranchOffices[]
): BranchOfficeLabels => {
  const labels: BranchOfficeLabels = {};

  (branchOffices ?? []).forEach((office) => {
    if (office?.BranchOfficeId === undefined || office?.BranchOfficeId === null) {
      return;
    }
    const key = normalizeId(office.BranchOfficeId);
    const label = labels[key] ?? { ufs: [], areas: [] };

    if (office.Uf && !label.ufs.includes(office.Uf)) label.ufs.push(office.Uf);
    if (office.AreaDescription && !label.areas.includes(office.AreaDescription)) {
      label.areas.push(office.AreaDescription);
    }

    labels[key] = label;
  });

  Object.keys(labels).forEach((key) => {
    labels[key].ufs.sort((a, b) => a.localeCompare(b));
    labels[key].areas.sort((a, b) => a.localeCompare(b));
  });

  return labels;
};

/**
 * Texto pesquisável da filial: UFs **e** nomes de área. O rótulo exibido colapsa as áreas numa
 * contagem (`PE · 8 áreas`), mas o operador busca pela cidade que conhece.
 */
export const branchOfficeSearchText = (label?: BranchOfficeLabel): string => {
  if (!label) return "";
  return label.ufs.concat(label.areas).join(" ");
};

/** `PE · 8 áreas` · `PE · Recife` (área única) · `-` sem dado. */
export const branchOfficeLabelText = (label?: BranchOfficeLabel): string => {
  if (!label) return "";
  const uf = label.ufs.join("/");
  if (!label.areas.length) return uf;
  if (label.areas.length === 1) return uf ? `${uf} · ${label.areas[0]}` : label.areas[0];
  const areas = `${label.areas.length} áreas`;
  return uf ? `${uf} · ${areas}` : areas;
};

/**
 * Uma chamada por montagem da tela. Falha, filial `Hidden` (a recarga inclui escondidas, o
 * endpoint `/not-hidden` não as devolve) ou registro ausente degradam para o código puro, **sem**
 * notificação de erro: é dado acessório, não pode bloquear nem poluir a tela.
 */
export const useBranchOfficeLabels = (): BranchOfficeLabels => {
  const apiUrl = useApiUrl();
  const [labels, setLabels] = useState<BranchOfficeLabels>({});

  useEffect(() => {
    let active = true;

    axiosInstance
      .get(`${apiUrl}/v1/branch-office/not-hidden`)
      .then((response) => {
        if (!active) return;
        const data = response?.data;
        setLabels(aggregateBranchOfficeLabels(Array.isArray(data) ? data : []));
      })
      .catch(() => {
        if (active) setLabels({});
      });

    return () => {
      active = false;
    };
  }, [apiUrl]);

  return labels;
};
